import React, { useState, useEffect, useCallback } from 'react';
import './DFSPage.css';
import logo from './assets/logo.png';

const API_BASE = process.env.REACT_APP_API_URL || '';
const SALARY_CAP = 50000;
const LINEUP_SIZE = 6;

const DFSPage = () => {
  const [races, setRaces] = useState([]);
  const [selectedRace, setSelectedRace] = useState(null);
  const [selectedSeries, setSelectedSeries] = useState('cup');
  const [projections, setProjections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Sorting state
  const [sortField, setSortField] = useState('projected_points');
  const [sortDirection, setSortDirection] = useState('desc');

  // Lineup builder state
  const [lineup, setLineup] = useState([]);
  const [lockedDrivers, setLockedDrivers] = useState(new Set());
  const [excludedDrivers, setExcludedDrivers] = useState(new Set());
  const [dominatorDrivers, setDominatorDrivers] = useState([]);
  const [exposureTargets, setExposureTargets] = useState({});

  // Multi-lineup state
  const [generatedLineups, setGeneratedLineups] = useState([]);
  const [lineupType, setLineupType] = useState(null); // 'gpp' or 'cash'
  const [isOptimizing, setIsOptimizing] = useState(false);

  // Strategy guide panel
  const [showStrategyGuide, setShowStrategyGuide] = useState(false);
  const [showRacePicks, setShowRacePicks] = useState(false);

  // Set page title
  useEffect(() => {
    document.title = 'DFS Optimizer | LapLeaders';
  }, []);

  // Fetch races on mount
  useEffect(() => {
    fetchUpcomingRaces();
  }, []);

  // Fetch projections when race changes
  const fetchProjections = useCallback(async (raceId) => {
    setLoading(true);
    setError(null);
    try {
      // Try post_practice first, fall back to pre_weekend
      let response = await fetch(
        `${API_BASE}/api/v1/dfs/races/${raceId}/projections?run_type=post_practice`
      );
      if (response.status === 404) {
        response = await fetch(
          `${API_BASE}/api/v1/dfs/races/${raceId}/projections?run_type=pre_weekend`
        );
      }
      if (response.ok) {
        const data = await response.json();
        setProjections(data.projections || []);
      } else if (response.status === 404) {
        setProjections([]);
        setError('No DFS projections available. Run simulation and import salaries first.');
      }
    } catch (err) {
      setError('Error loading projections');
      console.error(err);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (selectedRace) {
      fetchProjections(selectedRace);
      // Reset lineup when race changes
      setLineup([]);
      setLockedDrivers(new Set());
      setExcludedDrivers(new Set());
      setDominatorDrivers([]);
      setExposureTargets({});
    }
  }, [selectedRace, fetchProjections]);

  const fetchUpcomingRaces = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/v1/projections/upcoming-races?this_week_only=true`);
      if (response.ok) {
        const data = await response.json();
        setRaces(data);
        // Select first race of selected series
        const seriesRaces = data.filter(r => r.series_code === selectedSeries);
        if (seriesRaces.length > 0) {
          setSelectedRace(seriesRaces[0].race_id);
        } else if (data.length > 0) {
          // Fallback to first available race
          setSelectedRace(data[0].race_id);
          setSelectedSeries(data[0].series_code);
        }
      }
    } catch (err) {
      console.error('Error fetching races:', err);
    }
  };

  // Filter races by selected series
  const filteredRaces = races.filter(r => r.series_code === selectedSeries);

  // Handle series change
  const handleSeriesChange = (series) => {
    setSelectedSeries(series);
    const seriesRaces = races.filter(r => r.series_code === series);
    if (seriesRaces.length > 0) {
      setSelectedRace(seriesRaces[0].race_id);
    }
    // Reset lineup when series changes
    setLineup([]);
    setLockedDrivers(new Set());
    setExcludedDrivers(new Set());
    setDominatorDrivers(new Set());
    setExposureTargets({});
    setGeneratedLineups([]);
  };

  // Sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const sortedProjections = [...projections].sort((a, b) => {
    let aVal = a[sortField];
    let bVal = b[sortField];
    if (typeof aVal === 'string') {
      aVal = aVal.toLowerCase();
      bVal = bVal.toLowerCase();
    }
    if (sortDirection === 'asc') {
      return aVal > bVal ? 1 : -1;
    }
    return aVal < bVal ? 1 : -1;
  });

  // Lineup management
  const addToLineup = (driver) => {
    if (lineup.length >= LINEUP_SIZE) return;
    if (lineup.some(d => d.driver_id === driver.driver_id)) return;
    setLineup([...lineup, driver]);
  };

  const removeFromLineup = (driverId) => {
    setLineup(lineup.filter(d => d.driver_id !== driverId));
  };

  const clearLineup = () => {
    setLineup([]);
  };

  const toggleLock = (driverId) => {
    const newLocked = new Set(lockedDrivers);
    if (newLocked.has(driverId)) {
      newLocked.delete(driverId);
    } else {
      newLocked.add(driverId);
      // Remove from excluded if locked
      const newExcluded = new Set(excludedDrivers);
      newExcluded.delete(driverId);
      setExcludedDrivers(newExcluded);
    }
    setLockedDrivers(newLocked);
  };

  const toggleExclude = (driverId) => {
    const newExcluded = new Set(excludedDrivers);
    if (newExcluded.has(driverId)) {
      newExcluded.delete(driverId);
    } else {
      newExcluded.add(driverId);
      // Remove from locked if excluded
      const newLocked = new Set(lockedDrivers);
      newLocked.delete(driverId);
      setLockedDrivers(newLocked);
      // Remove from lineup if excluded
      setLineup(lineup.filter(d => d.driver_id !== driverId));
    }
    setExcludedDrivers(newExcluded);
  };

  const toggleDominator = (driverId) => {
    const idx = dominatorDrivers.indexOf(driverId);
    if (idx !== -1) {
      // Remove — remaining doms re-number automatically
      setDominatorDrivers(dominatorDrivers.filter(id => id !== driverId));
    } else {
      // Add to end — gets next priority number
      setDominatorDrivers([...dominatorDrivers, driverId]);
    }
  };

  // Calculate lineup totals
  const lineupSalary = lineup.reduce((sum, d) => sum + d.salary, 0);
  const lineupPoints = lineup.reduce((sum, d) => sum + d.projected_points, 0);
  const remainingBudget = SALARY_CAP - lineupSalary;

  // Optimize lineup
  const optimizeLineup = async () => {
    setIsOptimizing(true);
    setError(null);
    try {
      const response = await fetch(
        `${API_BASE}/api/v1/dfs/races/${selectedRace}/optimize?run_type=post_practice`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            locked_driver_ids: Array.from(lockedDrivers),
            excluded_driver_ids: Array.from(excludedDrivers),
            num_lineups: 1
          })
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.lineups && data.lineups.length > 0) {
          // Map optimized lineup to full driver data
          const optimizedDrivers = data.lineups[0].drivers.map(d => {
            const fullDriver = projections.find(p => p.driver_id === d.driver_id);
            return fullDriver || d;
          });
          setLineup(optimizedDrivers);
        }
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Optimization failed');
      }
    } catch (err) {
      setError('Error optimizing lineup');
      console.error(err);
    } finally {
      setIsOptimizing(false);
    }
  };

  // Check if user has made any selections (lock or exclude)
  const hasUserInput = lockedDrivers.size > 0 || excludedDrivers.size > 0 || dominatorDrivers.length > 0 || Object.keys(exposureTargets).length > 0;

  // Generate multiple lineups (GPP or Cash)
  const generateMultipleLineups = async (type, count) => {
    if (!hasUserInput) {
      setError('Please lock or exclude at least 1 driver before generating lineups. This ensures the lineups reflect your own analysis.');
      return;
    }
    const numLineups = count || (type === 'gpp' ? 10 : 3);
    setIsOptimizing(true);
    setError(null);
    setLineupType(type);
    try {
      // Use optimize endpoint with locked/excluded drivers
      const response = await fetch(
        `${API_BASE}/api/v1/dfs/races/${selectedRace}/optimize?run_type=post_practice`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            locked_driver_ids: Array.from(lockedDrivers),
            excluded_driver_ids: Array.from(excludedDrivers),
            dominator_driver_ids: dominatorDrivers.length > 0 ? dominatorDrivers : null,
            driver_exposure_targets: Object.keys(exposureTargets).length > 0 ? exposureTargets : null,
            num_lineups: numLineups,
            game_type: type,
            max_exposure: 0.5
          })
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.lineups && data.lineups.length > 0) {
          const mappedLineups = data.lineups.map(lu => ({
            ...lu,
            drivers: lu.drivers.map(d => {
              const fullDriver = projections.find(p => p.driver_id === d.driver_id);
              return fullDriver || d;
            })
          }));
          setGeneratedLineups(mappedLineups);
        }
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Generation failed');
      }
    } catch (err) {
      setError('Error generating lineups');
      console.error(err);
    } finally {
      setIsOptimizing(false);
    }
  };

  // Export all lineups to clipboard
  const exportAllLineups = () => {
    if (generatedLineups.length === 0) return;
    const lines = generatedLineups.map((lu, i) =>
      `Lineup ${i + 1}: ${lu.drivers.map(d => d.dk_id).join(',')}`
    ).join('\n');
    navigator.clipboard.writeText(lines).then(() => {
      alert(`${generatedLineups.length} lineups copied!`);
    });
  };

  // Download lineups as DraftKings import CSV
  const downloadDKImportCSV = () => {
    if (generatedLineups.length === 0) return;

    // DraftKings NASCAR import format: D,D,D,D,D,D header, then DK IDs
    const headers = ['D', 'D', 'D', 'D', 'D', 'D'];
    const rows = generatedLineups.map(lu =>
      lu.drivers.map(d => d.dk_id)
    );

    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);

    const raceName = races.find(r => r.race_id === selectedRace)?.race_name || 'lineups';
    const filename = `DK_Import_${raceName.replace(/\s+/g, '_')}_${lineupType}_${generatedLineups.length}_lineups.csv`;

    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Download projections as CSV
  const downloadProjectionsCSV = () => {
    if (projections.length === 0) return;

    const headers = ['Driver', 'Start', 'DK ID', 'Salary', 'Proj Points', 'Value', 'Avg Finish', 'Win %', 'Top 5 %', 'Top 10 %'];
    const rows = sortedProjections.map(d => [
      d.driver_name,
      d.start_position || '',
      d.dk_id,
      d.salary,
      d.projected_points.toFixed(1),
      d.value.toFixed(2),
      d.avg_finish.toFixed(1),
      d.win_probability.toFixed(1),
      d.top5_probability.toFixed(1),
      d.top10_probability.toFixed(1)
    ]);

    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);

    const raceName = races.find(r => r.race_id === selectedRace)?.race_name || 'projections';
    const filename = `${raceName.replace(/\s+/g, '_')}_projections.csv`;

    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Export lineup as DK format
  const exportLineup = () => {
    if (lineup.length !== LINEUP_SIZE) {
      alert('Please fill all 6 lineup slots before exporting');
      return;
    }

    const dkIds = lineup.map(d => d.dk_id).join('\n');
    navigator.clipboard.writeText(dkIds).then(() => {
      alert('Lineup IDs copied to clipboard!');
    }).catch(() => {
      // Fallback
      const text = lineup.map(d => `${d.driver_name}: ${d.dk_id}`).join('\n');
      alert('Copy these DK IDs:\n\n' + text);
    });
  };

  const renderSortIndicator = (field) => {
    if (sortField !== field) return null;
    return <span className="sort-indicator">{sortDirection === 'asc' ? '▲' : '▼'}</span>;
  };

  return (
    <div className="dfs-page">
      {/* Header */}
      <header className="dfs-header">
        <img src={logo} alt="LapLeaders" className="page-header-logo" />
        <h1>DFS Lineup Optimizer</h1>
        <p className="subtitle">DraftKings Lineups Powered by 10,000 Race Simulations</p>
        <div className="salary-cap-badge">
          Salary Cap: ${SALARY_CAP.toLocaleString()} | {LINEUP_SIZE} Drivers
        </div>
      </header>

      {/* Series Tabs */}
      <section className="series-tabs">
        <button
          className={`series-tab ${selectedSeries === 'cup' ? 'active' : ''}`}
          onClick={() => handleSeriesChange('cup')}
        >
          Cup Series
        </button>
        <button
          className={`series-tab ${selectedSeries === 'truck' ? 'active' : ''}`}
          onClick={() => handleSeriesChange('truck')}
        >
          Truck Series
        </button>
        <button
          className={`series-tab ${selectedSeries === 'xfinity' ? 'active' : ''}`}
          onClick={() => handleSeriesChange('xfinity')}
        >
          Xfinity Series
        </button>
      </section>

      {/* Race Selection */}
      <section className="race-selection">
        <div className="selection-row">
          <div className="select-group">
            <label>Select Race:</label>
            <select
              value={selectedRace || ''}
              onChange={(e) => setSelectedRace(Number(e.target.value))}
            >
              {filteredRaces.length > 0 ? (
                filteredRaces.map((race) => (
                  <option key={race.race_id} value={race.race_id}>
                    {race.race_name} @ {race.track_name}
                  </option>
                ))
              ) : (
                <option value="">No races this week</option>
              )}
            </select>
          </div>

        </div>
      </section>

      {/* Strategy Guide Panel */}
      <section className="strategy-guide-section">
        <button
          className={`strategy-guide-toggle ${showStrategyGuide ? 'open' : ''}`}
          onClick={() => setShowStrategyGuide(!showStrategyGuide)}
        >
          <span className="toggle-icon">{showStrategyGuide ? '\u25BC' : '\u25B6'}</span>
          How to Build GPP Lineups
        </button>
        {showStrategyGuide && (
          <div className="strategy-guide-content">
            <div className="strategy-guide-grid">

              <div className="strategy-card">
                <h3>DraftKings Scoring</h3>
                <ul>
                  <li><strong>Finish Position:</strong> 1st = 46 pts, 2nd = 42, 3rd = 41, down to 43rd = 1 pt</li>
                  <li><strong>Place Differential:</strong> +/- 1 pt per position gained or lost from starting position</li>
                  <li><strong>Laps Led:</strong> 0.25 pts per lap led</li>
                  <li><strong>Fastest Laps:</strong> 0.5 pts per fastest lap in the field</li>
                </ul>
                <p className="strategy-tip">A driver leading 100 laps earns 25 bonus pts on top of their finish. This is why dominators are critical.</p>
              </div>

              <div className="strategy-card">
                <h3>What is a Dominator?</h3>
                <ul>
                  <li>A driver expected to <strong>lead the most laps</strong> and rack up fastest laps</li>
                  <li>Dominator points (laps led + fastest laps) are the biggest DFS scoring separator</li>
                  <li>Look for: strong track history of laps led, front-row starter, fast in practice long runs</li>
                  <li>Every GPP lineup needs <strong>1-2 dominators</strong> to reach a winning score</li>
                </ul>
                <p className="strategy-tip">Use the gold "Dom" button to tag your dominator picks. The optimizer will rotate dominator pairs across your lineup portfolio.</p>
              </div>

              <div className="strategy-card">
                <h3>Place Differential</h3>
                <ul>
                  <li>A driver starting P25 who finishes P10 earns <strong>+15 bonus points</strong></li>
                  <li>Look for talented drivers who qualified poorly or had penalties</li>
                  <li>Practice speed matters: fast long-run speed + deep starting position = upside</li>
                  <li>Short tracks and road courses have the highest place diff variance</li>
                </ul>
                <p className="strategy-tip">The best GPP plays combine a strong finish with big place diff. A P10 finish from P30 scores more than a P10 from P5.</p>
              </div>

              <div className="strategy-card">
                <h3>GPP vs Cash Strategy</h3>
                <ul>
                  <li><strong>Cash:</strong> Play safe, high-floor drivers. Minimize risk. You just need to beat ~50% of the field</li>
                  <li><strong>GPP:</strong> Chase ceiling. You need to beat thousands of lineups, so differentiate</li>
                  <li>In GPP, pair 1-2 dominators with high-upside place diff plays and salary relief</li>
                  <li>Being different matters: low-owned drivers who hit give you massive leverage</li>
                </ul>
                <p className="strategy-tip">If everyone has the same lineup, you split the prize. GPP rewards unique, high-ceiling builds.</p>
              </div>

              <div className="strategy-card">
                <h3>Building a 20-Lineup Portfolio</h3>
                <ul>
                  <li>Select 3-5 dominator candidates and let the optimizer rotate pairs</li>
                  <li>Set <strong>exposure targets</strong> (the "%" field) to control how often a driver appears</li>
                  <li>Core drivers: 40-60% exposure. Leverage plays: 15-25%. Dart throws: 5-10%</li>
                  <li>No single driver should be in more than 60% of your lineups</li>
                </ul>
                <p className="strategy-tip">Lock must-have drivers, exclude drivers you want to fade, and set exposure % to build a balanced portfolio.</p>
              </div>

              <div className="strategy-card">
                <h3>Salary Construction</h3>
                <ul>
                  <li>$50,000 cap for 6 drivers. Average $8,333 per slot</li>
                  <li><strong>Studs + Duds:</strong> 2 premium ($10K+) dominators + 2-3 salary relief plays ($5-6K)</li>
                  <li><strong>Balanced:</strong> Spread salary more evenly with mid-range ($7-9K) core drivers</li>
                  <li>Value plays need a path to 20+ DK pts: strong place diff or a realistic top-15 finish</li>
                </ul>
                <p className="strategy-tip">Don't pay up for a driver just because they're fast. A $6K driver finishing P12 from P30 can outscore a $10K driver finishing P5 from P3.</p>
              </div>

            </div>
          </div>
        )}
      </section>

      {/* This Week's Race Overview */}
      {projections.length > 0 && (
      <section className="strategy-guide-section">
        <button
          className={`race-picks-toggle ${showRacePicks ? 'open' : ''}`}
          onClick={() => setShowRacePicks(!showRacePicks)}
        >
          <span className="toggle-icon">{showRacePicks ? '\u25BC' : '\u25B6'}</span>
          This Week: {races.find(r => r.race_id === selectedRace)?.race_name || 'Race'} at {races.find(r => r.race_id === selectedRace)?.track_name || 'TBD'} - Sim Overview
        </button>
        {showRacePicks && (
          <div className="race-picks-content">

            {/* Track Info Banner */}
            <div className="track-info-banner">
              <div className="track-stat">
                <span className="track-label">Track</span>
                <span className="track-value">{races.find(r => r.race_id === selectedRace)?.track_name || 'TBD'}</span>
              </div>
              <div className="track-stat">
                <span className="track-label">Series</span>
                <span className="track-value">{races.find(r => r.race_id === selectedRace)?.series_name || ''}</span>
              </div>
              <div className="track-stat">
                <span className="track-label">Field Size</span>
                <span className="track-value">{projections.length} drivers</span>
              </div>
              <div className="track-stat">
                <span className="track-label">Sim Status</span>
                <span className="track-value">{races.find(r => r.race_id === selectedRace)?.has_post_practice ? 'Post-Practice' : 'Pre-Weekend'}</span>
              </div>
            </div>

            {/* Track Context */}
            <div className="picks-tier">
              <p className="tier-desc" style={{fontSize: '0.95rem', lineHeight: '1.6'}}>
                Kansas Speedway is a 1.5-mile intermediate — 267 laps / 400 miles. The cleanest 2026 comparable is Vegas (also 1.5-mile) where Hamlin won with a 136.1 DR, Bell had 124.3, Byron 118.5, Larson 116.2, Elliott 111.1. Kansas practice ratified most of that: Reddick P1 (182.69), Wallace P2, Hamlin P3, Briscoe P4, Gibbs P5, Buescher P6, Suarez P7, Bell P8, Elliott P9. DK scoring at Kansas comes from <strong>laps led</strong> (0.25 pt/lap — ~20 pt ceiling for a dominator) and <strong>place differential</strong> (+1 pt/spot gained). Rule of the week: trust 2026 Vegas DR + Kansas practice speed over 3-year track history. A driver can be a "Kansas specialist" on paper and still be slow this weekend.
              </p>
            </div>

            {/* Dominator Candidates */}
            <div className="picks-tier">
              <div className="tier-header tier-dom">
                <h3>Dominator Candidates</h3>
                <span className="tier-badge dom-badge">Pick 2-3, order by priority</span>
              </div>

              <div className="driver-picks-grid">
                <div className="driver-pick primary-pick">
                  <div className="pick-rank">D1</div>
                  <div className="pick-details">
                    <div className="pick-name">Denny Hamlin <span className="pick-car">#11</span></div>
                    <div className="pick-meta">$11,000 | P2 Start | 34.4% Win | 78.3% Top 5</div>
                    <div className="pick-case">Highest composite on the slate (92.5) by a wide margin. Kansas Cup history: 6 races, 9.0 avg finish, <strong>55.0 avg laps led</strong>, 117.8 DR (highest in field), 89% top-15 laps. Qualified P2, practiced P3. P2 start kills place differential (max +1) so the entire build is laps-led + win points — and the sim says he lands the win 1 in 3 races. The chalk dominator, can't ignore at a Kansas where his lap-leading history is this strong.</div>
                  </div>
                </div>

                <div className="driver-pick primary-pick">
                  <div className="pick-rank">D2</div>
                  <div className="pick-details">
                    <div className="pick-name">Christopher Bell <span className="pick-car">#20</span></div>
                    <div className="pick-meta">$10,700 | P11 Start | 11.4% Win | 50.1% Top 5</div>
                    <div className="pick-case">Elite intermediate form all year: Vegas 2026 was 124.3 DR, 31 laps led, 99.3% top-15 from the pole. Kansas practice confirmed the speed (P8, 179.93 mph best). P11 qualifying is the gift — <strong>realistic +6 to +8 PD</strong> on top of laps-led + top-5 upside. Best dual-threat on the slate: can lead laps AND gain positions. At $10.7K he's the same tier as Larson but the P11 start unlocks PD math Larson can't get from P4.</div>
                  </div>
                </div>

                <div className="driver-pick primary-pick">
                  <div className="pick-rank">D3</div>
                  <div className="pick-details">
                    <div className="pick-name">Kyle Larson <span className="pick-car">#5</span></div>
                    <div className="pick-meta">$10,500 | P4 Start | 9.9% Win | 45.8% Top 5</div>
                    <div className="pick-case">Vegas 2026: 116.2 DR, 62 laps led from P5 start — still the most dominant 1.5-mile driver in the sport. But <strong>Kansas practice is a yellow flag: P25, 177.89 mph</strong>. Qualifying pulled him to P4, so the single-lap was there, but the long-run speed signal is soft vs. Hamlin/Bell. Still a dom — you don't fade a driver with his 1.5-mile ceiling — but consider him D2 or D3 rather than co-D1. P4 start means zero PD upside, so this is laps-led + finish points only. Lower exposure than Hamlin/Bell.</div>
                  </div>
                </div>

                <div className="driver-pick secondary-pick">
                  <div className="pick-rank">D4</div>
                  <div className="pick-details">
                    <div className="pick-name">Tyler Reddick <span className="pick-car">#45</span></div>
                    <div className="pick-meta">$9,500 | P1 (POLE) | 9.9% Win | 46.7% Top 5</div>
                    <div className="pick-case">Fastest car this weekend by the practice sheet: <strong>P1 at 182.69 mph</strong>, then the pole. Vegas 2026 was only 93.0 DR (P7→P13) so the 2026 intermediate profile is mixed, but Kansas-specific speed is undeniable. Starting P1 = zero PD upside, all laps-led + finish. At $9.5K you're buying current-week speed at a discount to the Hamlin/Bell/Larson tier. The speed play — not the history play.</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Mid-Range Core */}
            <div className="picks-tier">
              <div className="tier-header tier-mid">
                <h3>Core Plays</h3>
                <span className="tier-badge mid-badge">Pair with your doms</span>
              </div>

              <div className="driver-picks-grid">
                <div className="driver-pick">
                  <div className="pick-rank pick-rank-mid">CORE</div>
                  <div className="pick-details">
                    <div className="pick-name">Chase Elliott <span className="pick-car">#9</span></div>
                    <div className="pick-meta">$9,000 | P13 Start | 7.9% Win | 41.8% Top 5</div>
                    <div className="pick-case">All signals aligned: Kansas Cup 6.8 avg finish (tied best in field), Vegas 2026 P15→P2 with 111.1 DR, Kansas practice P9 at 179.78 mph. P13 qualifying unlocks <strong>+6 to +10 PD</strong> on top of a top-5 projection. At $9K this is the best salary-adjusted play on the board. Must-play in cash, high GPP exposure.</div>
                  </div>
                </div>

                <div className="driver-pick">
                  <div className="pick-rank pick-rank-mid">CORE</div>
                  <div className="pick-details">
                    <div className="pick-name">William Byron <span className="pick-car">#24</span></div>
                    <div className="pick-meta">$10,000 | P14 Start | 5.2% Win | 31.6% Top 5</div>
                    <div className="pick-case">Vegas 2026: P9→P3 with 118.5 DR, 26 laps led, +8 PD — dominant 1.5-mile result. Kansas history is solid (6 races, 12.7 avg fin, 91.2 DR). Kansas practice P19 is the one soft note, but Vegas says the car is clearly top-5 caliber at 1.5s. P14 start gives real PD room (+8 realistic). At $10K the price is fair — diversification off the top tier without giving up ceiling.</div>
                  </div>
                </div>

                <div className="driver-pick">
                  <div className="pick-rank pick-rank-mid">SPEED</div>
                  <div className="pick-details">
                    <div className="pick-name">Ty Gibbs <span className="pick-car">#54</span></div>
                    <div className="pick-meta">$9,200 | P3 Start | 1.6% Win | 14.3% Top 5</div>
                    <div className="pick-case">The sim undersells him. <strong>Vegas 2026: 98.8 DR, +14 PD</strong> from P3 finished P5 — real 1.5-mile speed this year. Kansas confirmed it: P5 practice (180.35 mph), P3 qualifying. Kansas history says he's been bad here (23.0 avg finish) but the 2026 car is clearly different equipment. Starting P3 = no PD but real laps-led shot if the car holds all day. At $9.2K this is a contrarian core play as the field will fade him on the stale history.</div>
                  </div>
                </div>

                <div className="driver-pick">
                  <div className="pick-rank pick-rank-mid">SPEED</div>
                  <div className="pick-details">
                    <div className="pick-name">Chase Briscoe <span className="pick-car">#19</span></div>
                    <div className="pick-meta">$9,800 | P5 Start | 1.3% Win | 11.9% Top 5</div>
                    <div className="pick-case">Vegas 2026: <strong>P18→P8, +15 PD, 87.5 DR</strong> — best pure passer in that field. Kansas ratified: P4 practice (180.96 mph), P5 qualifying. All current-form signals say top-10 car. Kansas Cup history (17.3 avg finish) is stale — he's driving different equipment this year. P5 start caps PD upside but the win/top-5 ceiling is real. The sim's 1.3% win is conservative given the practice + Vegas combo.</div>
                  </div>
                </div>

                <div className="driver-pick">
                  <div className="pick-rank pick-rank-mid">SLEEPER</div>
                  <div className="pick-details">
                    <div className="pick-name">Bubba Wallace <span className="pick-car">#23</span></div>
                    <div className="pick-meta">$8,500 | P10 Start | 2.4% Win | 19.4% Top 5</div>
                    <div className="pick-case">Most under-the-radar play on the slate. <strong>Kansas practice P2 (182.03 mph)</strong>, Vegas 2026 98.3 DR with 99.3% top-15 laps (finished P9 despite the pace). Kansas history is weak (18.0 avg finish) but the 2026 speed at 1.5s is elite. P10 qualifying is the disconnect — car is clearly faster than that. <strong>Best ceiling-per-dollar play</strong> outside the top tier. GPP contrarian at $8.5K.</div>
                  </div>
                </div>

                <div className="driver-pick">
                  <div className="pick-rank pick-rank-mid">CORE</div>
                  <div className="pick-details">
                    <div className="pick-name">Chris Buescher <span className="pick-car">#17</span></div>
                    <div className="pick-meta">$8,300 | P7 Start | 3.2% Win | 22.1% Top 5</div>
                    <div className="pick-case">Consistent all year: Vegas 2026 98.1 DR (P10→P6), Kansas practice P6 (180.27 mph), P7 qual. Kansas history is middling (13.3 avg fin, 87.1 DR) but recent form is better. P7 start caps PD (+3 realistic) so this is a floor play — top-10 projection at $8.3K. Cash-game staple, GPP role-player behind the high-ceiling picks.</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Value Plays */}
            <div className="picks-tier">
              <div className="tier-header tier-value">
                <h3>Value / Salary Relief</h3>
                <span className="tier-badge value-badge">Under $7.5K | 1-2 per lineup</span>
              </div>

              <div className="driver-picks-grid">
                <div className="driver-pick">
                  <div className="pick-rank pick-rank-val">PD</div>
                  <div className="pick-details">
                    <div className="pick-name">Alex Bowman <span className="pick-car">#48</span></div>
                    <div className="pick-meta">$7,600 | P33 Start | 1.1% Win | 11.6% Top 5</div>
                    <div className="pick-case">The PD play of the slate once you filter for current form. Kansas Cup history: 11.2 avg finish, 88.4 DR, 75% top-15. Kansas practice P13 (179.12 mph) — car has pace this weekend. Starting P33 = <strong>+20+ PD ceiling</strong> at $7.6K if he runs to history. Safer DNF profile (8.1%) than most backmarker starts. Unlocks a Hamlin/Bell/Larson premium stack.</div>
                  </div>
                </div>

                <div className="driver-pick">
                  <div className="pick-rank pick-rank-val">SPEED</div>
                  <div className="pick-details">
                    <div className="pick-name">Daniel Suárez <span className="pick-car">#7</span></div>
                    <div className="pick-meta">$6,700 | P8 Start | 0.6% Win | 7.8% Top 5</div>
                    <div className="pick-case">Contrarian speed play. <strong>Kansas practice P7 (180.04 mph) — top-10 car.</strong> Vegas 2026 was poor (66.2 DR, P13→P18) so the 1.5-mile form isn't clean, but the Kansas-specific practice speed stands out at this price. P8 qual supports. At $6.7K you're getting top-10 practice pace for sub-$7K money. High DNF risk (13.5%) is the tax. GPP sleeper.</div>
                  </div>
                </div>

                <div className="driver-pick">
                  <div className="pick-rank pick-rank-val">VAL</div>
                  <div className="pick-details">
                    <div className="pick-name">Ryan Preece <span className="pick-car">#60</span></div>
                    <div className="pick-meta">$7,500 | P12 Start | 1.0% Win | 9.9% Top 5</div>
                    <div className="pick-case">Vegas 2026: 89.8 DR, P8→P11, 98.5% top-15 laps — ran steady. Kansas practice didn't crack top-20 but the qualifying P12 is fine. Limited PD (+3 to +5) from P12, top-15 floor. Connector piece when you need $7.5K salary without a dart throw.</div>
                  </div>
                </div>

                <div className="driver-pick">
                  <div className="pick-rank pick-rank-val">VAL</div>
                  <div className="pick-details">
                    <div className="pick-name">Noah Gragson <span className="pick-car">#4</span></div>
                    <div className="pick-meta">$5,400 | P28 Start | 0.35% Win | 3.6% Top 5</div>
                    <div className="pick-case">Min-salary dart. Vegas 2026 was rough (44.9 DR, P34→P30) so the 1.5-mile form isn't there. Kansas practice P14 is a small positive. P28 start gives PD room but DNF risk is 15.2%. At $5.4K he's a salary-unlock piece only — use when the build needs the $5K range to stack two premium doms. Low exposure, floor risk.</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Fades */}
            <div className="picks-tier">
              <div className="tier-header" style={{background: 'rgba(180,30,30,0.15)', borderLeft: '3px solid #b41e1e'}}>
                <h3 style={{color: '#e05555'}}>Fades</h3>
                <span className="tier-badge" style={{background: 'rgba(180,30,30,0.3)', color: '#e05555'}}>Avoid or limit exposure</span>
              </div>
              <div className="driver-picks-grid">
                <div className="driver-pick">
                  <div className="pick-rank" style={{background: 'rgba(180,30,30,0.3)', color: '#e05555'}}>FADE</div>
                  <div className="pick-details">
                    <div className="pick-name">Ross Chastain <span className="pick-car">#1</span></div>
                    <div className="pick-meta">$8,100 | P31 Start | 2.4% Win | 19.3% Top 5</div>
                    <div className="pick-case">Looks like a PD dream (P31 start, Kansas history 11.2 avg finish) but every 2026 signal says the car is slow. <strong>Vegas 2026: 63.3 DR, only 18.7% top-15 laps, -2 PD, P17→P17.</strong> Kansas practice P23 (178.04 mph) — not top-20. The P31 qual is real; he's a backmarker this weekend. A bad-car PD play gains 5 spots, not 20 — at $8.1K you're paying for history that the current form isn't delivering.</div>
                  </div>
                </div>

                <div className="driver-pick">
                  <div className="pick-rank" style={{background: 'rgba(180,30,30,0.3)', color: '#e05555'}}>FADE</div>
                  <div className="pick-details">
                    <div className="pick-name">Kyle Busch <span className="pick-car">#8</span></div>
                    <div className="pick-meta">$7,300 | P23 Start | 0.5% Win | 6.7% Top 5</div>
                    <div className="pick-case">Vegas 2026 DR was <strong>48.8 — worst of any driver priced $7K+</strong>. Kansas Cup history has faded to 18.2 avg finish, 77.3 DR. Kansas practice didn't crack top-20. P23 start looks like PD material but the car isn't capable of running to Kansas history right now. High DNF (11.9%). Plenty of better sub-$8K options with current-form signals (Preece, Suárez, Bowman).</div>
                  </div>
                </div>

                <div className="driver-pick">
                  <div className="pick-rank" style={{background: 'rgba(180,30,30,0.3)', color: '#e05555'}}>FADE</div>
                  <div className="pick-details">
                    <div className="pick-name">Austin Cindric <span className="pick-car">#2</span></div>
                    <div className="pick-meta">$7,200 | P34 Start | 0.05% Win | 1.5% Top 5</div>
                    <div className="pick-case">P34 start looks like PD bait but the car isn't there. Vegas 2026: 67.7 DR, P31→P19, +12 PD in a fuel-strategy game. Kansas practice P17 at 178.64 mph — outside the top half of the speed chart. Kansas history: 6 races, 29.0 avg finish (worst in field). At $7.2K you can get Suárez ($6.7K, P7 practice) for less and more upside.</div>
                  </div>
                </div>

                <div className="driver-pick">
                  <div className="pick-rank" style={{background: 'rgba(180,30,30,0.3)', color: '#e05555'}}>LIMIT</div>
                  <div className="pick-details">
                    <div className="pick-name">Joey Logano <span className="pick-car">#22</span></div>
                    <div className="pick-meta">$8,700 | P15 Start | 0.8% Win | 6.6% Top 5</div>
                    <div className="pick-case">Vegas 2026: 72.8 DR, only 59.2% top-15 laps — current 1.5-mile form is soft. Kansas Cup history is 14.8 avg finish, 79.2 DR — not terrible but not supporting $8.7K. Practice didn't make the top 20. P15 qual offers +5 PD ceiling but the salary is priced for ceiling, not floor. Buescher ($8.3K, P7) and Wallace ($8.5K, P10) both have better current-form signals. Limit to 5%.</div>
                  </div>
                </div>

                <div className="driver-pick">
                  <div className="pick-rank" style={{background: 'rgba(180,30,30,0.3)', color: '#e05555'}}>LIMIT</div>
                  <div className="pick-details">
                    <div className="pick-name">Carson Hocevar <span className="pick-car">#77</span></div>
                    <div className="pick-meta">$7,700 | P6 Start | 0.4% Win | 4.9% Top 5</div>
                    <div className="pick-case">Mixed signals, priced high. Current speed is real — Kansas practice P10 (179.52 mph), qualified P6 — but Vegas 2026 was poor (69.7 DR, P19→P22, -3 PD) and Kansas history is the worst in the field (26.2 avg finish, 66.5 DR). The P6 start kills PD math. You're paying $7.7K for a single-source speed signal (this weekend's qualifying). If you believe the Kansas qual, this is a contrarian GPP play — but not a core build. Limit 5-10%.</div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        )}
      </section>
      )}

      {/* Main Content */}
      <div className="dfs-content">
        {/* Projections Table */}
        <section className="projections-section">
          <div className="section-header">
            <h2>Driver Projections</h2>
            {!hasUserInput && projections.length > 0 && (
              <div className="user-input-hint">
                Lock or exclude at least 1 driver to generate lineups
              </div>
            )}
            <div className="optimizer-buttons">
              <button
                className="optimize-btn"
                onClick={optimizeLineup}
                disabled={projections.length === 0 || isOptimizing}
              >
                {isOptimizing ? 'Optimizing...' : 'Single Lineup'}
              </button>
              <button
                className="optimize-btn gpp-btn"
                onClick={() => generateMultipleLineups('gpp', 10)}
                disabled={projections.length === 0 || !hasUserInput || isOptimizing}
                title={!hasUserInput ? 'Lock or exclude at least 1 driver first' : ''}
              >
                {isOptimizing ? 'Optimizing...' : '10 GPP Lineups'}
              </button>
              <button
                className="optimize-btn gpp-btn gpp-20-btn"
                onClick={() => generateMultipleLineups('gpp', 20)}
                disabled={projections.length === 0 || !hasUserInput || isOptimizing}
                title={!hasUserInput ? 'Lock or exclude at least 1 driver first' : ''}
              >
                {isOptimizing ? 'Optimizing...' : '20 GPP Lineups'}
              </button>
              <button
                className="optimize-btn cash-btn"
                onClick={() => generateMultipleLineups('cash')}
                disabled={projections.length === 0 || !hasUserInput || isOptimizing}
                title={!hasUserInput ? 'Lock or exclude at least 1 driver first' : ''}
              >
                3 Cash Lineups
              </button>
              <button
                className="download-btn"
                onClick={downloadProjectionsCSV}
                disabled={projections.length === 0}
              >
                Download CSV
              </button>
            </div>
          </div>

          {error && <div className="error-message">{error}</div>}
          {loading && (
            <div className="loading">
              <div className="racing-spinner"></div>
              <span>Loading projections...</span>
            </div>
          )}

          {!loading && projections.length > 0 && (
            <div className="table-container">
              <table className="dfs-table">
                <thead>
                  <tr>
                    <th onClick={() => handleSort('driver_name')} className={sortField === 'driver_name' ? 'sorted' : ''}>
                      Driver {renderSortIndicator('driver_name')}
                    </th>
                    <th onClick={() => handleSort('start_position')} className={sortField === 'start_position' ? 'sorted' : ''}>
                      Start {renderSortIndicator('start_position')}
                    </th>
                    <th onClick={() => handleSort('salary')} className={sortField === 'salary' ? 'sorted' : ''}>
                      Salary {renderSortIndicator('salary')}
                    </th>
                    <th onClick={() => handleSort('projected_points')} className={sortField === 'projected_points' ? 'sorted' : ''}>
                      Proj Pts {renderSortIndicator('projected_points')}
                    </th>
                    <th onClick={() => handleSort('value')} className={sortField === 'value' ? 'sorted' : ''}>
                      Value {renderSortIndicator('value')}
                    </th>
                    <th onClick={() => handleSort('avg_finish')} className={sortField === 'avg_finish' ? 'sorted' : ''}>
                      Avg Fin {renderSortIndicator('avg_finish')}
                    </th>
                    <th onClick={() => handleSort('win_probability')} className={sortField === 'win_probability' ? 'sorted' : ''}>
                      Win % {renderSortIndicator('win_probability')}
                    </th>
                    <th onClick={() => handleSort('top5_probability')} className={sortField === 'top5_probability' ? 'sorted' : ''}>
                      Top 5 % {renderSortIndicator('top5_probability')}
                    </th>
                    <th onClick={() => handleSort('top10_probability')} className={sortField === 'top10_probability' ? 'sorted' : ''}>
                      Top 10 % {renderSortIndicator('top10_probability')}
                    </th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedProjections.map((driver) => {
                    const isInLineup = lineup.some(d => d.driver_id === driver.driver_id);
                    const isLocked = lockedDrivers.has(driver.driver_id);
                    const isExcluded = excludedDrivers.has(driver.driver_id);
                    const domPriority = dominatorDrivers.indexOf(driver.driver_id);
                    const isDominator = domPriority !== -1;

                    return (
                      <tr
                        key={driver.driver_id}
                        className={`${isInLineup ? 'selected' : ''} ${isExcluded ? 'excluded' : ''}`}
                        onClick={() => !isExcluded && addToLineup(driver)}
                        style={{ cursor: isExcluded ? 'not-allowed' : 'pointer' }}
                      >
                        <td className="driver-name">{driver.driver_name}</td>
                        <td className="start-pos">{driver.start_position ? `P${driver.start_position}` : '-'}</td>
                        <td className="salary">${driver.salary.toLocaleString()}</td>
                        <td className="projected-pts">{driver.projected_points.toFixed(1)}</td>
                        <td className="value">{driver.value.toFixed(2)}</td>
                        <td className="avg-finish">{driver.avg_finish.toFixed(1)}</td>
                        <td className="win-prob">{driver.win_probability.toFixed(1)}%</td>
                        <td className="top5-prob">{driver.top5_probability.toFixed(1)}%</td>
                        <td className="top10-prob">{driver.top10_probability.toFixed(1)}%</td>
                        <td className="action-btns" onClick={(e) => e.stopPropagation()}>
                          <button
                            className={`lock-btn ${isLocked ? 'active' : ''}`}
                            onClick={() => toggleLock(driver.driver_id)}
                            title="Lock in lineup"
                          >
                            {isLocked ? 'Locked' : 'Lock'}
                          </button>
                          <button
                            className={`exclude-btn ${isExcluded ? 'active' : ''}`}
                            onClick={() => toggleExclude(driver.driver_id)}
                            title="Exclude from lineup"
                          >
                            {isExcluded ? 'Out' : 'Exclude'}
                          </button>
                          <button
                            className={`dom-btn ${isDominator ? 'active' : ''}`}
                            onClick={() => toggleDominator(driver.driver_id)}
                            title={isDominator ? `Dominator priority #${domPriority + 1} (click to remove)` : 'Tag as dominator for GPP (order = priority)'}
                          >
                            {isDominator ? `D${domPriority + 1}` : 'Dom'}
                          </button>
                          <input
                            type="number"
                            className="exposure-input"
                            placeholder="%"
                            min="0"
                            max="100"
                            step="5"
                            value={exposureTargets[driver.driver_id] ? Math.round(exposureTargets[driver.driver_id] * 100) : ''}
                            onChange={(e) => {
                              const val = e.target.value;
                              const newTargets = { ...exposureTargets };
                              if (val === '' || val === '0') {
                                delete newTargets[driver.driver_id];
                              } else {
                                newTargets[driver.driver_id] = Math.min(100, Math.max(0, parseInt(val))) / 100;
                              }
                              setExposureTargets(newTargets);
                            }}
                            title="Target exposure % for GPP lineups"
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {!loading && projections.length === 0 && !error && (
            <div className="no-data">No projections available for this race.</div>
          )}
        </section>

        {/* Lineup Builder */}
        <section className="lineup-section">
          <h2>Your Lineup</h2>

          <div className="lineup-card">
            <div className="lineup-title">
              <h3>{lineup.length}/{LINEUP_SIZE} Drivers</h3>
              {lineup.length > 0 && (
                <button className="clear-btn" onClick={clearLineup}>
                  Clear All
                </button>
              )}
            </div>

            <div className="lineup-slots">
              {[...Array(LINEUP_SIZE)].map((_, index) => {
                const driver = lineup[index];
                return (
                  <div key={index} className={`lineup-slot ${driver ? 'filled' : ''}`}>
                    <span className="slot-number">{index + 1}</span>
                    <div className="slot-content">
                      {driver ? (
                        <>
                          <span className="slot-driver">{driver.driver_name}</span>
                          <span>
                            <span className="slot-salary">${driver.salary.toLocaleString()}</span>
                            <span className="slot-points">{driver.projected_points.toFixed(1)} pts</span>
                          </span>
                        </>
                      ) : (
                        <span className="slot-empty">Click a driver to add</span>
                      )}
                    </div>
                    {driver && (
                      <button
                        className="remove-slot-btn"
                        onClick={() => removeFromLineup(driver.driver_id)}
                      >
                        ×
                      </button>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="lineup-totals">
              <div className="total-row salary">
                <span>Total Salary</span>
                <span>${lineupSalary.toLocaleString()}</span>
              </div>
              <div className={`total-row remaining ${remainingBudget < 0 ? 'over-budget' : ''}`}>
                <span>Remaining</span>
                <span>${remainingBudget.toLocaleString()}</span>
              </div>
              <div className="total-row points">
                <span>Projected Points</span>
                <span>{lineupPoints.toFixed(1)}</span>
              </div>
            </div>
          </div>

          <div className="export-section">
            <button
              className="export-btn"
              onClick={exportLineup}
              disabled={lineup.length !== LINEUP_SIZE || remainingBudget < 0}
            >
              Copy Lineup to Clipboard
            </button>
          </div>
        </section>
      </div>

      {/* Generated Lineups Section */}
      {generatedLineups.length > 0 && (
        <section className="generated-lineups-section">
          <div className="generated-header">
            <h2>{lineupType === 'gpp' ? 'GPP' : 'Cash'} Lineups ({generatedLineups.length})</h2>
            <div className="generated-header-buttons">
              <button className="export-btn" onClick={exportAllLineups}>
                Copy All
              </button>
              <button className="dk-import-btn" onClick={downloadDKImportCSV}>
                Download DK Import CSV
              </button>
            </div>
          </div>
          {(() => {
            const counts = {};
            generatedLineups.forEach(lu => {
              lu.drivers.forEach(d => {
                if (!counts[d.driver_id]) {
                  counts[d.driver_id] = { name: d.driver_name, salary: d.salary, count: 0 };
                }
                counts[d.driver_id].count += 1;
              });
            });
            const total = generatedLineups.length;
            const rows = Object.values(counts).sort((a, b) => b.count - a.count);
            return (
              <div className="exposure-summary">
                <h3>Driver Exposure</h3>
                <div className="exposure-grid">
                  {rows.map(r => {
                    const pct = (r.count / total) * 100;
                    return (
                      <div key={r.name} className="exposure-row">
                        <span className="exposure-name">{r.name}</span>
                        <span className="exposure-bar-wrap">
                          <span className="exposure-bar" style={{ width: `${pct}%` }} />
                        </span>
                        <span className="exposure-pct">{pct.toFixed(0)}% ({r.count}/{total})</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}
          <div className="lineups-grid">
            {generatedLineups.map((lu, index) => (
              <div key={index} className="generated-lineup-card">
                <div className="lineup-card-header">
                  <span>Lineup {index + 1}</span>
                  <span className="lineup-card-pts">{lu.total_points} pts</span>
                </div>
                <div className="lineup-card-drivers">
                  {lu.drivers.map(d => (
                    <div key={d.driver_id} className="lineup-card-driver">
                      <span>{d.driver_name}</span>
                      <span>${d.salary.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
                <div className="lineup-card-footer">
                  <span>Salary: ${lu.total_salary.toLocaleString()}</span>
                  <span>Left: ${lu.remaining_budget.toLocaleString()}</span>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="dfs-footer">
        <p>Projections based on 10,000 race simulations</p>
        <p>Value = Projected Points per $1,000 salary</p>
        <div className="legal-disclaimer">
          <p>
            <strong>Disclaimer:</strong> LapLeaders provides projections for entertainment and informational
            purposes only. We do not guarantee winning results. Past performance does not guarantee
            future results. You are responsible for your own lineup decisions. Must be 21+ to play
            daily fantasy sports in most states. Gambling problem? Call 1-800-GAMBLER.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default DFSPage;
